import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Users,
  DollarSign,
  Target,
  BookOpen,
  UserCheck,
  Clock,
  BarChart3,
  Shield,
  Globe,
  TrendingUp,
  Calendar,
  Settings,
  CheckCircle,
  ArrowRight,
  Zap
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

const UKGDetail = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/");
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const ukgModules = [
    { icon: Users, name: "UKG Kronos Pro Workforce Management", description: "Advanced time tracking, scheduling optimization, labor forecasting, and workforce analytics" },
    { icon: DollarSign, name: "UKG Kronos Pro Payroll", description: "Global payroll processing, tax compliance, benefits administration, and compensation management" },
    { icon: Target, name: "UKG Kronos Pro Talent Management", description: "Performance management, succession planning, career development, and goal tracking" },
    { icon: BookOpen, name: "UKG Kronos Pro Learning", description: "Training programs, compliance tracking, skill development, and certification management" },
    { icon: UserCheck, name: "UKG Kronos Pro Recruiting", description: "Applicant tracking, interview management, onboarding workflows, and talent pipeline" },
    { icon: Clock, name: "UKG Kronos Dimensions", description: "Advanced scheduling, labor forecasting, workforce analytics, and productivity insights" },
    { icon: BarChart3, name: "UKG Kronos People Analytics", description: "Workforce insights, predictive analytics, reporting dashboards, and trend analysis" },
    { icon: Shield, name: "UKG Kronos Security & Compliance", description: "Data protection, audit trails, regulatory compliance, and security monitoring" },
    { icon: Globe, name: "UKG Kronos Ready", description: "Small business HR solutions, payroll processing, and time tracking for growing organizations" },
    { icon: Settings, name: "UKG Kronos Integration Hub", description: "Third-party integrations, API management, data synchronization, and system connectivity" },
    { icon: Calendar, name: "Employee Self-Service", description: "Profile management, time-off requests, pay statements, and mobile access" },
    { icon: Users, name: "Manager Self-Service", description: "Team management, approval workflows, reporting tools, and dashboard analytics" }
  ];

  const implementationPhases = [
    {
      phase: "Foundation Setup",
      duration: "4-6 weeks",
      activities: ["Organizational structure mapping", "Employee data migration", "Payroll configuration", "System integration planning"]
    },
    {
      phase: "Core Modules",
      duration: "6-10 weeks",
      activities: ["Time & attendance setup", "Benefits administration", "Performance management", "Reporting configuration"]
    },
    {
      phase: "Advanced Features",
      duration: "4-8 weeks",
      activities: ["Workforce analytics", "Mobile enablement", "Advanced scheduling", "Talent management setup"]
    },
    {
      phase: "Go-Live & Support",
      duration: "2-4 weeks",
      activities: ["User training", "System testing", "Production deployment", "Post-implementation support"]
    }
  ];

  const benefits = [
    { metric: "Labor Cost Management", value: "Improved visibility and control", icon: DollarSign },
    { metric: "Compliance Tracking", value: "Automated audit trails", icon: Shield },
    { metric: "Scheduling Efficiency", value: "Streamlined workflows", icon: Clock },
    { metric: "Employee Experience", value: "Self-service capabilities", icon: Users }
  ];

  const industries = [
    { name: "Healthcare", description: "Complex scheduling, compliance tracking, multi-facility management" },
    { name: "Retail", description: "Seasonal workforce, shift optimization, customer demand planning" },
    { name: "Manufacturing", description: "Production scheduling, safety compliance, shift differentials" },
    { name: "Hospitality", description: "Variable schedules, tip management, seasonal staffing" },
    { name: "Public Sector", description: "Union rules, complex benefits, regulatory compliance" },
    { name: "Financial Services", description: "Risk management, audit trails, performance tracking" }
  ];

  return (
    <>
      <SEOHead 
        title="UKG Kronos (Ultimate Kronos Group) Implementation Services | Smartborn"
        description="Expert UKG Kronos workforce management, HR, and payroll implementation services. Optimize your workforce with UKG Kronos Pro, Dimensions, and Ready solutions from certified consultants."
        keywords="UKG Kronos implementation, Ultimate Kronos Group, workforce management, UKG Kronos Pro, UKG Kronos Dimensions, payroll, time tracking, HR solutions"
        canonical="/ukg-detail"
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-16">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-background via-background to-primary/5">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <Badge className="mb-6 bg-secondary/10 text-secondary border-secondary/20">
                  UKG Kronos Experts
                </Badge>
                <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-primary bg-clip-text text-transparent">
                  UKG Kronos Workforce Solutions
                </h1>
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Transform your workforce management with UKG Kronos's comprehensive HR, payroll, and talent solutions. Optimize scheduling, improve compliance, and enhance employee experience.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" onClick={handleGetStarted} className="bg-gradient-primary hover:opacity-90">
                    Transform Your Workforce
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => navigate("/")}>
                    View All Solutions
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Implementation Approach */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Workforce Transformation Methodology
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Structured approach to optimize your workforce operations and employee experience
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {implementationPhases.map((phase, index) => (
                  <Card key={index} className="relative bg-gradient-card border-0 shadow-elegant">
                    <CardHeader className="text-center pb-4">
                      <div className="bg-gradient-primary text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                        {index + 1}
                      </div>
                      <CardTitle className="text-lg font-semibold text-foreground">{phase.phase}</CardTitle>
                      <Badge variant="outline" className="mx-auto">{phase.duration}</Badge>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {phase.activities.map((activity, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle className={`h-4 w-4 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                            {activity}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* UKG Modules */}
          <section className="py-20 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Complete UKG Kronos Solution Suite
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Comprehensive workforce management from time tracking to talent development
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {ukgModules.map((module, index) => (
                  <Card key={index} className="group hover:shadow-elegant transition-all duration-300 bg-gradient-card border-0">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-gradient-primary p-3 rounded-lg group-hover:shadow-glow transition-all duration-300">
                          <module.icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground mb-3 text-lg">
                            {module.name}
                          </h3>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {module.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Industry Solutions */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Industry-Specific Solutions
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Tailored UKG Kronos implementations for industry-specific challenges
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {industries.map((industry, index) => (
                  <Card key={index} className="group hover:shadow-elegant transition-all duration-300 bg-gradient-card border-0">
                    <CardContent className="p-6 text-center">
                      <div className="bg-gradient-primary p-3 rounded-lg group-hover:shadow-glow transition-all duration-300 inline-flex mb-4">
                        <Target className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="font-semibold text-foreground mb-3 text-lg">
                        {industry.name}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {industry.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Workforce ROI */}
          <section className="py-20 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Proven Workforce ROI
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Measurable impact on workforce efficiency and employee satisfaction
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="text-center bg-gradient-card border-0 shadow-elegant">
                    <CardContent className="p-8">
                      <benefit.icon className={`h-12 w-12 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mx-auto mb-4`} />
                      <h3 className="font-semibold text-foreground mb-2 text-lg">
                        {benefit.metric}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {benefit.value}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary/5 via-secondary/5 to-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Ready to Optimize Your Workforce?
                </h2>
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Partner with UKG Kronos certified experts to transform your workforce management and unlock your organization's human potential.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" onClick={handleGetStarted} className="bg-gradient-primary hover:opacity-90">
                    Schedule Workforce Assessment
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => navigate("/")}>
                    Explore More Solutions
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default UKGDetail;